/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import ReactSlider from 'react-slider';
import isEmpty from 'lodash.isempty';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { formatMoney } from '../../util/helpers/numeralHelpers';
import Paragraph from '../Paragraph/Paragraph';
import useStepPost from '../../hooks/useStepPost';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import { metalStreamDataHelper } from '../../util/helpers/metalStreamDataHelper';
import useMetalStreamAllocationDispatch from '../../hooks/useMetalStreamAllocationDispatch';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import { Range } from '../Fields/Range';
import InputText from '../Fields/InputText';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import { getApplication } from '../../store/actions/application/applicationActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';

const formValidationSchema = Yup.object({
  ...getValidationSchema({
    fields: ['GoldPercentage', 'SilverPercentage', 'GoldPercentage'],
  }),
});

const getSliderTrackStyles = (index) => {
  if (index === 2) {
    return themeColors.colorPrimaryDark;
  }

  if (index === 1) {
    return themeColors.colorProgressBarDefault;
  }

  return `linear-gradient(270deg, ${themeColors.colorPrimaryDark}, ${themeColors.colorPrimaryLight})`;
};

const RangeContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 50px;
  align-items: center;
  margin-bottom: 10px;

  & > p:last-child {
    justify-self: end;
  }
`;
const PercentageContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;
const PercentFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > p {
    margin-top: 5px;
    font-weight: 600;
  }
`;
export const Slider = styled(ReactSlider)`
  height: 8px;
`;

export const SliderThumb = styled.div`
  border: none;
  padding: 0;
  background-color: transparent;
  &,
  &:active,
  &:focus {
    outline: none;
  }
  width: 20px;
  height: 20px;
  border: 4px solid ${themeColors.colorPrimary};
  box-shadow: 0 3px 8px 0 rgba(112, 120, 135, 0.24);
  border-radius: 100%;
  background-color: #f4f4f4;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;

export const SliderTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${({ index, theme }) => getSliderTrackStyles(index, theme)};
`;

const MetalStreamAllocateForm = ({ nextPath, onModalClose }) => {
  const [silverPrice, setSilverPrice] = useState(0);
  const [goldPrice, setGoldPrice] = useState(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ApplicationUid } = useApplicationUid();
  const { metalStreamAllocationDispatch } = useMetalStreamAllocationDispatch();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const { MetalStreamData } = useMetalStreamData();
  const Amount = metalStreamDataHelper(MetalStreamData, 'Amount');
  const goldPercentage = metalStreamDataHelper(
    MetalStreamData,
    'GoldPercentage',
  );
  const { handleSubmit, control, watch, setValue, setError } = useForm({
    resolver: yupResolver(formValidationSchema),
    defaultValues: {
      GoldPercentage:
        goldPercentage || goldPercentage === 0 ? goldPercentage : 50,
      SilverPercentage: 100 - goldPercentage,
      percentage: goldPercentage,
    },
  });

  const { gtmStepEvent } = useGtmStepHook();

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'MetalStream allocate funds',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'MetalStream allocate funds',
    });
  };

  const [percentage, GoldPercentage, SilverPercentage] = watch([
    'percentage',
    'GoldPercentage',
    'SilverPercentage',
  ]);
  const onGetApplicationSuccess = (application) => {
    if (isEmpty(application)) {
      return;
    }
    onSuccess();
  };
  const onAPISuccess = () => {
    dispatch(
      getApplication({
        data: { ApplicationUid },
        onSuccess: onGetApplicationSuccess,
      }),
    );
    handleGtmEvent();
  };

  const onSubmit = ({ GoldPercentage, SilverPercentage }) => {
    const onError = (errorMessage) => {
      setError('GoldPercentage', errorMessage);
      setError('SilverPercentage', errorMessage);
    };

    metalStreamAllocationDispatch({
      data: {
        GoldPercentage: GoldPercentage / 100,
        SilverPercentage: SilverPercentage / 100,
      },
      onSuccess: onAPISuccess,
      onError,
    });
  };

  useEffect(() => {
    setValue('GoldPercentage', percentage, { shouldValidate: true });
    setValue('SilverPercentage', 100 - percentage, { shouldValidate: true });
    setGoldPrice((Amount * (percentage / 100)).toFixed(2));
    setSilverPrice((Amount * ((100 - percentage) / 100)).toFixed(2));
  }, [percentage, Amount, setValue]);

  useEffect(() => {
    if (GoldPercentage) {
      setValue('percentage', GoldPercentage);
      setValue('GoldPercentage', GoldPercentage);
      setValue('SilverPercentage', 100 - GoldPercentage);
    }
  }, [GoldPercentage, setValue]);

  useEffect(() => {
    if (SilverPercentage) {
      setValue('percentage', 100 - SilverPercentage);
      setValue('GoldPercentage', 100 - SilverPercentage);
      setValue('SilverPercentage', SilverPercentage);
    }
  }, [SilverPercentage, setValue]);

  useEffect(() => {
    if (GoldPercentage > 100) {
      setValue('GoldPercentage', 100);
      setValue('SilverPercentage', 0);
    }
    if (GoldPercentage < 0) {
      setValue('GoldPercentage', 0);
      setValue('SilverPercentage', 100);
    }
  }, [GoldPercentage, setValue]);

  const handleSliderChange = () => (value) => {
    setValue('percentage', value);
    setValue('GoldPercentage', value);
    setValue('SilverPercentage', 100 - value);
  };

  return (
    <FormWrap>
      <Paragraph mb={4} color={themeColors.colorTextXLLight}>
        {t('metalstream.monthlyInvestment')}:
      </Paragraph>
      <Paragraph>{formatMoney(Amount)}</Paragraph>
      <FormContainer width="100%" isColumn>
        <RangeContainer>
          <Paragraph weight={400}>{t('metalstream.goldRange')}</Paragraph>
          <Controller
            control={control}
            render={({ field }) => (
              <Slider
                renderThumb={(props, state) => <SliderThumb {...props} />}
                renderTrack={(props, state) => (
                  <SliderTrack index={state.index} {...props} />
                )}
                name="percentage"
                value={percentage}
                onChange={handleSliderChange()}
                min={0}
                max={100}
                withTracks
              />
            )}
            name="percentage"
          />
          <Paragraph weight={400}>{t('metalstream.silverRange')}</Paragraph>
        </RangeContainer>
        <PercentageContainer>
          <PercentFieldContainer>
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  {...field}
                  {...fieldState}
                  width="90px"
                  suffix="%"
                  textAlign="center"
                  fontSize={variables.typography.p.regular.fontSize}
                  min="0"
                  max="100"
                  type="number"
                />
              )}
              name="GoldPercentage"
            />
            {!!goldPrice && (
              <Paragraph weight={400}>{formatMoney(goldPrice)}</Paragraph>
            )}
          </PercentFieldContainer>
          <PercentFieldContainer>
            <Controller
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  {...field}
                  {...fieldState}
                  suffix="%"
                  width="90px"
                  textAlign="center"
                  fontSize={variables.typography.p.regular.fontSize}
                  min="0"
                  max="100"
                  type="number"
                />
              )}
              name="SilverPercentage"
            />
            {!!silverPrice && (
              <Paragraph weight={400}>{formatMoney(silverPrice)}</Paragraph>
            )}
          </PercentFieldContainer>
        </PercentageContainer>
      </FormContainer>

      <OnBoardingCta type="submit" onClick={handleSubmit(onSubmit)} />
    </FormWrap>
  );
};

MetalStreamAllocateForm.propTypes = {
  nextPath: PropTypes.string,
  onModalClose: PropTypes.func,
};

export default MetalStreamAllocateForm;
